import { useCallback, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { Trans, useTranslation } from 'next-i18next'
import {
  EVENTS,
  sendContactSimilarListingsTrackingEvent,
  useTrackingWithSort,
  VARIANTS
} from '@/api/tracking'
import {
  Box,
  Button,
  Dialog,
  IconCheckCircle,
  IconInfoInverted,
  Inline,
  List,
  ResponsiveRender,
  Stack,
  Text,
  TextLinkButton,
  TooltipRenderer,
  useDialogControllers,
  useResponsiveValue,
  useToast
} from '@machobear/design-system'
import { type TurnstileInstance } from '@marsidev/react-turnstile'
import { Formik } from 'formik'
import { omit } from 'lodash'
import noop from 'lodash/noop'
import toNumber from 'lodash/toNumber'
import { useDispatch } from 'react-redux'

import config from '@/config'
import { usePublicSession } from '@/models/session/hooks'
import { startPublicChat, tenantStartChat } from '@/ports'
import Yup from '@/lib/yup'
import { isEmptyOrNull } from '@/utils'
import { getUrl } from '@/utils/listings'
import useCooldown from '@/hooks/use-cooldown'
import useGoogleEventUserData from '@/hooks/use-google-event-user-data'
import useRole from '@/hooks/use-role'
import useSessionComplete from '@/hooks/use-session-complete'
import DSLoginModal from '@/features/Auth/login-modal/ds-login-modal'
import { StyledForm, Terms } from '@/features/Listings/contact-form'
import {
  contactSimilarListingFunc,
  saveCustomMessage
} from '@/features/Listings/contact-form/inquiryMessageUtil'
import { useInquiryMessage } from '@/features/Listings/contact-form/useInquiryMessage'
import { useAddRecentMessage } from '@/features/Listings/hooks/recent-messages'
import { useAlerts } from '@/components/alert/useAlerts.tsx'
import CdnImage from '@/components/cdn-image'
import { TextAreaField } from '@/components/ds-form/textarea-field'
import { SuccessModal } from '@/components/ds-success-modal'
import { Error, Group, Row } from '@/components/form'
import { VerifiedListingBadge } from '@/components/icons'
import ListingTypeText from '@/components/listing-type-text'
import Skeleton from '@/components/skeleton'

import { Captcha } from '../captcha/captcha'
import { FieldCheckbox } from '../ds-form/check-box-field'
import DateDropdownField from '../ds-form/date-dropdown-field'
import { FieldInput } from '../ds-form/text-field'
import PriceDisplay from './pricing-display'
import * as styles from './styles.css'
import type { Listing } from './types'
import { getIsRented, getPrice, renderAddress } from './utils'

type ModalProps = {
  listing: Listing
  visible: boolean
  onClose: () => void
  onComplete?: () => void
}

type FormProps = {
  listingId: string
  isRented: boolean
  isLoggedIn: boolean
  isMobile: boolean
  visible: {
    register: boolean
    registerTenant: boolean
    success: boolean
  }
  onSuccess: (parms: {
    formValues: any
    contact_similar_listings: boolean
  }) => void
  onError: (params: {
    code: string
    data: {
      formValues: any
      listingId: string
      contact_similar_listings: boolean
    }
  }) => void
  clickId?: string
}

const DSMessageModal = ({
  visible,
  onClose,
  listing,
  onComplete = noop
}: ModalProps) => {
  const { t } = useTranslation()
  const isMobile = useResponsiveValue()({
    desktop: false,
    tablet: false,
    mobile: true
  })
  const isLoggedIn = useSessionComplete()
  const { sendInquiryMessageAfterLogin, showInquiryMessageToast } =
    useInquiryMessage(isLoggedIn)
  const { sendAlert } = useAlerts()
  const dispatch = useDispatch()
  const router = useRouter()
  const isCityPage = router.asPath.includes('city')

  const {
    visible: innerModalVisible,
    showModal: innerShowModal,
    closeModal: innerCloseModal
  } = useDialogControllers({
    login: false,
    register: false,
    registerTenant: false,
    success: false
  })

  const isRented = getIsRented(listing)
  const price = getPrice(listing)
  const frequency = listing.price_frequency
  const listingId = listing.listing_id

  const address = renderAddress(listing)
  const bedroomCount = toNumber(listing.bedrooms)
  const bathroomCount = toNumber(listing.bathrooms)
  const listingSize = toNumber(listing.size)
  const unitType = listing.unit_type_txt_id
  const unitScope = listing.unit_type_scope_txt_id

  const city = listing.city
  const unitImageFiles = listing.unit_files?.filter(
    file => !file.tag?.includes('video')
  )

  const cover =
    listing.cover_photo?.aws_s3_key || listing.cover_photo_aws_s3_key
  const images = !isEmptyOrNull(unitImageFiles)
    ? unitImageFiles
    : [{ aws_s3_key: cover }]

  const image = images[0].aws_s3_key
  const link = getUrl(listing)
  const isVerified = listing.verified_state_machine === 'approved'

  const [inquiryData, setInquiryData] = useState<{
    formValues: any
    listingId: string
    contact_similar_listings: boolean
  }>()

  const ListingInfo = () => {
    return (
      <Box display="flex" alignItems="center" className={styles.detailRow}>
        <Text tone="secondary">
          {bedroomCount > 0
            ? t('b.listing.view.count_beds.text', {
                defaultValue: '{{ bedroomCount }} Bed',
                bedroomCount
              })
            : t('common.studio', 'Studio')}
        </Text>
        <Text tone="secondary">·</Text>
        <Text tone="secondary">
          {t('b.listing.view.count_baths.text', {
            defaultValue: '{{ bathroomCount }} Bath',
            bathroomCount
          })}
        </Text>
        {listingSize > 0 && (
          <>
            <Text tone="secondary">·</Text>
            <Text tone="secondary">
              {t('b.listing.view.size.text', {
                defaultValue: '{{ listingSize }} ft²',
                listingSize
              })}
            </Text>
          </>
        )}
      </Box>
    )
  }

  const content = isLoggedIn ? (
    <Text tone="tertiary" size="medium" align="center">
      {t(
        'b.listing.view.message_sent.text',
        'The message was sent to the landlord.'
      )}
    </Text>
  ) : (
    <List type="icon" icon={<IconCheckCircle tone="positive" />} space="small">
      <Text>
        {t(
          'r.complete_registration.create.receive_faster_response.text',
          'Faster responses from landlords'
        )}
      </Text>
      <Text>
        {t(
          'r.complete_registration.create.chat_instantly.text',
          'Chat instantly'
        )}
      </Text>
      <Text>
        {t(
          'r.complete_registration.create.1_click_apply.text',
          'Apply to listings with one click'
        )}
      </Text>
      <Text>
        {t(
          'r.complete_registration.create.built_in_safety_features.text',
          'Built-in safety features'
        )}
      </Text>
    </List>
  )

  const userData = useGoogleEventUserData()
  const { mutateAsync: track } = useTrackingWithSort()

  const sendTracking = useCallback(
    (
      formValues: {
        email: string
        first_name: string
        last_name: string
      },
      contact_similar_listings: boolean
    ) => {
      track({
        event_name: isLoggedIn
          ? EVENTS.INQUIRY_SENT
          : EVENTS.PUBLIC_CHAT_STARTED,
        metadata: { listingId, source: 'listing-card-modal' },
        variant_group: VARIANTS.LISTING_SORT,
        referer: document.referrer
      })
      const eventData = isLoggedIn
        ? {
            event: `logged-${
              isCityPage ? 'city-page-' : ''
            }inquire-submit-sent`,
            ...userData
          }
        : {
            event: `listing-${
              isCityPage ? 'city-page-' : ''
            }inquire-submit-sent`,
            userEmail: formValues.email,
            userFirstName: formValues.first_name,
            userLastName: formValues.last_name
          }
      window.dataLayer.push(eventData)

      sendContactSimilarListingsTrackingEvent({
        isChecked: contact_similar_listings,
        isCityPage
      })
    },
    [isLoggedIn, isCityPage, listingId, track, userData]
  )

  const afterLoginFn = useCallback(
    async (inquiryData: any) => {
      await sendInquiryMessageAfterLogin(() => {
        if (inquiryData?.contact_similar_listings) {
          sendAlert(listing)
        }
        showInquiryMessageToast()
        sendTracking(
          {
            email: inquiryData?.formValues?.email,
            first_name: inquiryData?.formValues?.first_name,
            last_name: inquiryData?.formValues?.last_name
          },
          inquiryData?.contact_similar_listings
        )
      }, inquiryData)
    },
    [
      listing,
      sendAlert,
      sendInquiryMessageAfterLogin,
      showInquiryMessageToast,
      sendTracking
    ]
  )

  return (
    <>
      <Dialog
        open={visible}
        onClose={onClose}
        id={`message-modal-for-listing-${listingId}`}
        width="large"
        title={t('b.listing.view.message_modal.title', 'Check Availability')}
      >
        <Box display="flex" flexDirection={{ mobile: 'column', tablet: 'row' }}>
          <Box className={styles.leftSectionMessageModal}>
            <ResponsiveRender above="mobile">
              <Box
                className={styles.photoContainer}
                overflow="hidden"
                borderRadius="large"
              >
                <CdnImage
                  src={image ?? ''}
                  sizes={'345px'}
                  layout="fill"
                  objectFit="cover"
                  placeholder={'shimmer'}
                />
              </Box>
            </ResponsiveRender>

            <Box
              paddingLeft={isMobile ? 'none' : 'small'}
              paddingBottom={isMobile ? 'none' : 'small'}
              marginTop={isMobile ? 'none' : 'small'}
            >
              <Stack space="xxsmall">
                <Inline alignY="center" space="small">
                  <Text size="xlarge" weight="strong">
                    {address}
                  </Text>
                  {isVerified && (
                    <Box
                      display="flex"
                      alignItems="center"
                      className={styles.verifiedBadgeWrapper}
                    >
                      <VerifiedListingBadge />
                    </Box>
                  )}
                </Inline>
                <Inline space="small" alignY="center">
                  <Inline alignY="center" space="xxsmall">
                    <Text size="small" tone="secondary">
                      {city}
                    </Text>
                    <Text size="small" tone="secondary">
                      ·
                    </Text>

                    {isMobile ? (
                      <ListingInfo />
                    ) : (
                      <ListingTypeText
                        unitType={unitType}
                        unitScope={unitScope}
                      />
                    )}
                  </Inline>
                </Inline>
              </Stack>

              <Box paddingTop="small">
                <Box
                  display="flex"
                  justifyContent="flexStart"
                  alignItems="flexEnd"
                >
                  <PriceDisplay
                    price={price}
                    frequency={frequency}
                    fallback={isRented && t('common.rented', 'Rented')}
                    loading={false}
                    showRange={false}
                  />
                </Box>
                <ResponsiveRender above="mobile">
                  <Box marginTop="xxxsmall">
                    <ListingInfo />
                  </Box>
                </ResponsiveRender>
              </Box>

              <ResponsiveRender above="mobile">
                <Box paddingTop="small">
                  <Text size="medium">
                    <TextLinkButton
                      onClick={() => window.open(link, '_blank')}
                      id={
                        isLoggedIn
                          ? 'logged-view-listing-details-click'
                          : 'listing-view-listing-details-click'
                      }
                    >
                      {t('b.listing.view.view_listing.button', 'View listing')}
                    </TextLinkButton>
                  </Text>
                </Box>
              </ResponsiveRender>
            </Box>
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flexStart"
            marginTop={isMobile ? 'small' : 'none'}
            paddingLeft={{ mobile: 'none', tablet: 'gutter' }}
            className={styles.rightSectionMessageModal}
          >
            <ContactFormBody
              listingId={listingId}
              isRented={isRented}
              onSuccess={async ({ formValues, contact_similar_listings }) => {
                onClose()
                onComplete?.() // to notify grandparent modal like multi-list building
                if (isLoggedIn) {
                  if (contact_similar_listings) {
                    await sendAlert(listing)
                  }
                  showInquiryMessageToast()
                } else {
                  showInquiryMessageToast({
                    listing,
                    formValues,
                    contact_similar_listings
                  })
                }
                sendTracking(formValues, contact_similar_listings)
              }}
              onError={({ code, data }) => {
                onClose()
                // onComplete() // closes building listing modal in the background
                // calling this removes login modal immediately because
                // login modal lives here (ds-message-modal) and closing its parent
                // takes it out of dom along with it.

                // the proper way to destroy login modal is to lift it high up, and
                // wrap it in a context provider with callback to close it.
                if (code === 'err.public_chat_login_required') {
                  setInquiryData(data)
                  innerShowModal('login')()
                }
              }}
              isLoggedIn={isLoggedIn}
              isMobile={isMobile ?? false}
              visible={innerModalVisible}
            />
            <Terms />
          </Box>
        </Box>
      </Dialog>
      <DSLoginModal
        t={t}
        mode="register"
        visible={innerModalVisible.register}
        onClose={innerCloseModal('register')}
        eventId="web-signup-submit-inquire-sendmessage"
        listingId={parseInt(listingId)}
      />
      <DSLoginModal
        t={t}
        mode="registerTenant"
        visible={innerModalVisible.registerTenant}
        onClose={innerCloseModal('registerTenant')}
        eventId="web-signup-submit-inquire-sendmessage"
        listingId={parseInt(listingId)}
      />
      <DSLoginModal
        t={t}
        mode="login"
        visible={innerModalVisible.login}
        onClose={innerCloseModal('login')}
        eventId="web-login-submit-inquire-sendmessage"
        listingId={parseInt(listingId)}
        data={{
          username: inquiryData?.formValues?.email,
          first_name: inquiryData?.formValues?.first_name,
          last_name: inquiryData?.formValues?.last_name
        }}
        afterLogin={() => afterLoginFn(inquiryData)}
      />
      <SuccessModal
        dialogId="message-successfully-sent"
        content={content}
        open={innerModalVisible.success}
        onClose={innerCloseModal('success')}
        title={t('b.listing.view.message_sent.title', 'Message Sent!')}
        description={
          !isLoggedIn ? (
            <Text>
              <Trans
                i18nKey="r.complete_registration.create.check_email_to_verify.text"
                t={t}
              >
                Please check your email to verify your email address.
                <br />
                With a liv.rent account, gain access to:
              </Trans>
            </Text>
          ) : null
        }
        buttonText={
          !isLoggedIn &&
          t(
            'b.listing.view.complete_registration.text',
            'Complete Registration'
          )
        }
        onSubmit={
          !isLoggedIn
            ? async () => {
                await dispatch.session.loadChatPublicSession()
                innerShowModal('registerTenant')()
              }
            : null
        }
      />
    </>
  )
}

const ContactFormBody = ({
  listingId,
  onSuccess,
  onError,
  isRented,
  isLoggedIn
}: FormProps) => {
  const { t } = useTranslation()
  const role = useRole()
  const isTenant = role === 'tenant'
  const dispatch = useDispatch()
  const captchaRef = useRef<TurnstileInstance | null>(null)
  const showToast = useToast()
  // formik's isSubmitting is toggled off at the end of submission and makes submit button tempoarily enabled before closing
  // customSubmitting is required to take over that behaviour and keep button disabled until modal is closed
  const [customSubmitting, setCustomSubmitting] = useState(false)

  const { addRecentMessage } = useAddRecentMessage()

  const isMobile = useResponsiveValue()({
    desktop: false,
    tablet: false,
    mobile: true
  })
  const inquiryMessageHook = useInquiryMessage(isLoggedIn)
  const { message, defaultMessage } = inquiryMessageHook

  useEffect(() => {
    if (isLoggedIn) return
    dispatch.session.loadChatPublicSession()
  }, [dispatch.session, isLoggedIn])

  const publicSession = usePublicSession()

  const { isCooling, startCooldown, timeLeft } = useCooldown()

  const onSubmit = async (values: any) => {
    setCustomSubmitting(true)
    saveCustomMessage({
      isLoggedIn: isLoggedIn,
      inquiryMessage: message ?? defaultMessage,
      formValues: values
    })
    const contact_similar_listings = values.contact_similar_listings
    values = omit(values, 'contact_similar_listings')
    values.message = values.message.trim()

    // TODO sendInquiryMessage
    const { response, body }: any = isLoggedIn
      ? await tenantStartChat(values, { listingId })
      : await startPublicChat({ body: values }, { listingId }, undefined, {
          headers: {
            'X-Captcha-Token': values.captcha
          }
        })

    captchaRef.current?.reset?.()

    if (!response.ok) {
      // if user email is already in our system
      if (body.message_key === 'err.public_chat_login_required') {
        onError({
          code: 'err.public_chat_login_required',
          data: { formValues: values, listingId, contact_similar_listings }
        })
        return
      }

      // TODO message_key must be more specific
      if (body.message_key === 'msg.general_error_request') {
        setCustomSubmitting(false)
        startCooldown(20)
        showToast({
          tone: 'info',
          message:
            body?.message ??
            t(
              'common.rate_limit_error',
              "It looks like you're sending messages a bit too quickly. Please pause for a short moment and try again."
            )
        })
        return
      }

      showToast({
        tone: 'critical',
        message: body?.message ?? 'Unable to contact the listing(s)'
      })
      setCustomSubmitting(false)
      return
    }

    // > onSuccess
    addRecentMessage(listingId)

    if (!contact_similar_listings) {
      onSuccess({ formValues: values, contact_similar_listings })
      return
    }

    const contactResult = await contactSimilarListingFunc({
      isLoggedIn,
      listingId,
      formValues: values
    })
    if (!contactResult.ok) {
      showToast({
        tone: 'critical',
        message:
          contactResult.message ?? 'Unable to contact the similar listings'
      })
    } else {
      // TODO > onSuccess
      if ((contactResult.successful_listings ?? []).length > 0) {
        contactResult.successful_listings?.forEach(listingId => {
          addRecentMessage(listingId)
        })
      }
      onSuccess({ formValues: values, contact_similar_listings })
    }
  }

  const initialValues = {
    message: (inquiryMessageHook.message as { content?: string })?.content,
    move_in_date: 'asap',
    contact_similar_listings: true,
    ...(!isLoggedIn
      ? {
          first_name: publicSession.first_name || '',
          last_name: publicSession.last_name || '',
          email: publicSession.username || '',
          captcha: ''
        }
      : {})
  }

  const validationSchema = Yup.object().shape({
    message: Yup.string().required(),
    move_in_date: Yup.string().required(),
    ...(!isLoggedIn
      ? {
          first_name: Yup.string().required(),
          last_name: Yup.string().required(),
          email: Yup.string().email().required(),
          captcha: Yup.string().test(
            'captcha-required',
            "Captcha is required. Please wait while it's validating.",
            function (value) {
              return !shouldValidateCaptcha || !!value
            }
          )
        }
      : {})
  })

  const isDisabled = (isLoggedIn && !isTenant) || isRented
  const shouldValidateCaptcha = config.ENABLE_CONTACT_FORM_CAPTCHA && !isRented
  const bottomGap = 15

  // must add !customSubmitting to prevent the skeleton from showing
  // while submission is stilling going
  if (!customSubmitting && inquiryMessageHook.isLoading) {
    return (
      <>
        <form noValidate style={{ width: '100%' }}>
          <Skeleton height={60} bottom={bottomGap} />
          <Skeleton height={120} bottom={bottomGap} />
          <Button type="submit" disabled={true}>
            {t('b.appointment.modal.send_message.button', 'Send Message')}
          </Button>
        </form>
      </>
    )
  }

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize={true}
    >
      {({
        values,
        dirty,
        isValid,
        setFieldValue,
        setFieldError,
        setFieldTouched
      }) => {
        const contactSimilarListingsCheckbox = (
          <FieldCheckbox
            name="contact_similar_listings"
            label={
              isMobile
                ? t('Contact similar listings')
                : t('Contact similar listings on my behalf')
            }
            checked={values.contact_similar_listings}
            badge={
              <TooltipRenderer
                id="contact_similar_listings_tooltip"
                tooltip={
                  <Stack space="small">
                    <Text>{t('If checked, liv.rent will')}:</Text>
                    <List>
                      <Text>
                        {t(
                          'Automatically message up to 3 similar listings on your behalf'
                        )}
                      </Text>
                      <Text>
                        {t(
                          'Notify you of similar, newly listed properties via email'
                        )}
                      </Text>
                    </List>
                  </Stack>
                }
              >
                {({ triggerProps }) => (
                  <Box display="inline" aria-label="Tooltip" {...triggerProps}>
                    <IconInfoInverted tone="tertiary" />
                  </Box>
                )}
              </TooltipRenderer>
            }
          />
        )

        if (isLoggedIn) {
          return (
            <StyledForm noValidate>
              <Row>
                <Group col={12} bottom={bottomGap}>
                  <DateDropdownField
                    name="move_in_date"
                    label={t('Move-in Date')}
                    disabled={isDisabled}
                  />
                </Group>
              </Row>
              <Row>
                <Group col={12} bottom={bottomGap}>
                  <TextAreaField
                    name="message"
                    label={t('Message')}
                    required
                    disabled={isDisabled}
                    lines={isMobile ? 4 : 6}
                  />
                </Group>
              </Row>
              <Row>
                <Group col={12} bottom={bottomGap}>
                  {contactSimilarListingsCheckbox}
                </Group>
              </Row>
              <Box>
                <Button
                  type="submit"
                  id={`logged-inquire-submit-${listingId}`}
                  disabled={
                    customSubmitting ||
                    (dirty && !isValid) ||
                    isDisabled ||
                    isCooling
                  }
                >
                  {customSubmitting
                    ? t('Submitting...')
                    : isCooling
                    ? t(
                        'common.try_again_cooldown',
                        'Try again ({{cooldownSeconds}}s)',
                        { cooldownSeconds: timeLeft }
                      )
                    : t(
                        'b.appointment.modal.send_message.button',
                        'Send Message'
                      )}
                </Button>
              </Box>
            </StyledForm>
          )
        }

        return (
          <StyledForm noValidate>
            <Row>
              <Group col={isMobile ? 12 : 6} bottom={bottomGap}>
                <FieldInput
                  name="first_name"
                  disabled={isDisabled}
                  autoComplete="section-contact-form given-name"
                  label={t('common.first_name', 'First name')}
                  required
                />
              </Group>
              <Group col={isMobile ? 12 : 6} bottom={bottomGap}>
                <FieldInput
                  name="last_name"
                  disabled={isDisabled}
                  autoComplete="section-contact-form family-name"
                  label={t('common.last_name', 'Last name')}
                  required
                />
              </Group>
            </Row>
            <Row>
              <Group col={isMobile ? 12 : 6} bottom={bottomGap}>
                <FieldInput
                  name="email"
                  disabled={isDisabled}
                  autoComplete="section-contact-form username email"
                  label={t('common.email', 'Email')}
                  required
                />
              </Group>
              <Group col={isMobile ? 12 : 6} bottom={bottomGap}>
                <DateDropdownField
                  name="move_in_date"
                  label={t('Move-in Date')}
                  disabled={isDisabled}
                />
              </Group>
            </Row>
            <Row>
              <Group col={12}>
                <TextAreaField
                  name="message"
                  label={t('Message')}
                  required
                  disabled={isDisabled}
                  lines={isMobile ? 4 : 6}
                />
              </Group>
            </Row>
            {shouldValidateCaptcha && (
              <Row>
                <Group col={12} bottom={bottomGap}>
                  <Captcha
                    id="message-modal-captcha"
                    ref={captchaRef}
                    onSuccess={captcha => setFieldValue('captcha', captcha)}
                    onExpire={() => setFieldValue('captcha', '')}
                    onError={() => {
                      setFieldValue('captcha', '', false)
                      setFieldTouched('captcha', true, false)
                      setFieldError(
                        'captcha',
                        t('Captcha validation failed. Please try again.')
                      )
                    }}
                    options={{ action: 'message-modal' }}
                  />
                  <Error name="captcha" />
                </Group>
              </Row>
            )}
            <Row>
              <Group col={12} bottom={bottomGap}>
                {contactSimilarListingsCheckbox}
              </Group>
            </Row>
            <Box>
              <Button
                type="submit"
                id={`listing-inquire-submit-${listingId}`}
                disabled={
                  customSubmitting ||
                  !dirty ||
                  (dirty && !isValid) ||
                  isDisabled ||
                  isCooling
                }
              >
                {customSubmitting
                  ? t('Submitting...')
                  : isCooling
                  ? t(
                      'common.try_again_cooldown',
                      'Try again ({{cooldownSeconds}}s)',
                      { cooldownSeconds: timeLeft }
                    )
                  : t(
                      'b.appointment.modal.send_message.button',
                      'Send Message'
                    )}
              </Button>
            </Box>
          </StyledForm>
        )
      }}
    </Formik>
  )
}

export default DSMessageModal
